import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getDocsFromServer, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db, functions } from '../../../../../firebase-client';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { parse, startOfWeek, format, getDay } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  formatLocalDateISO,
  formatUKDateISO,
  ukDateISOToLocalDate
} from '../../../../../utils/ukDates';
import { formatSailingBoatMoney } from '../../../utils/sailingBoatPayments';

// --- Localiser (GB, Monday week start) ---
const locales = {
  'en-GB': enGB
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), {
    weekStartsOn: 1
  }),
  getDay,
  locales
});
const DEFAULT_CALENDAR_LOCALE = 'en-GB';
const COUNTRY_TO_CALENDAR_LOCALE = {
  uk: 'en-GB',
  gb: 'en-GB',
  'united kingdom': 'en-GB',
  'great britain': 'en-GB',
  britain: 'en-GB',
  england: 'en-GB',
  scotland: 'en-GB',
  wales: 'en-GB',
  'northern ireland': 'en-GB',
  us: 'en-US',
  usa: 'en-US',
  'united states': 'en-US',
  'united states of america': 'en-US',
  au: 'en-AU',
  australia: 'en-AU',
  nz: 'en-NZ',
  'new zealand': 'en-NZ',
  ca: 'en-CA',
  canada: 'en-CA',
  ie: 'en-IE',
  ireland: 'en-IE',
  fr: 'fr-FR',
  france: 'fr-FR',
  de: 'de-DE',
  germany: 'de-DE',
  es: 'es-ES',
  spain: 'es-ES',
  it: 'it-IT',
  italy: 'it-IT',
  nl: 'nl-NL',
  netherlands: 'nl-NL',
  be: 'fr-BE',
  belgium: 'fr-BE',
  ch: 'de-CH',
  switzerland: 'de-CH'
};
function normalizeCountryKey(value = '') {
  return String(value).trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
}
function localeForCountry(country) {
  const key = normalizeCountryKey(country);
  if (!key) return DEFAULT_CALENDAR_LOCALE;
  if (/^[a-z]{2}$/i.test(key)) return COUNTRY_TO_CALENDAR_LOCALE[key] || `en-${key.toUpperCase()}`;
  if (COUNTRY_TO_CALENDAR_LOCALE[key]) return COUNTRY_TO_CALENDAR_LOCALE[key];
  const embeddedCode = String(country || '').match(/\b([A-Za-z]{2})\b(?!.*\b[A-Za-z]{2}\b)/);
  if (embeddedCode) {
    const code = embeddedCode[1].toLowerCase();
    return COUNTRY_TO_CALENDAR_LOCALE[code] || `en-${code.toUpperCase()}`;
  }
  return DEFAULT_CALENDAR_LOCALE;
}
function formatClubDate(date, locale, options) {
  try {
    return new Intl.DateTimeFormat(locale || DEFAULT_CALENDAR_LOCALE, options).format(date);
  } catch {
    return new Intl.DateTimeFormat(DEFAULT_CALENDAR_LOCALE, options).format(date);
  }
}
function buildBoatCalendarFormats(locale) {
  return {
    agendaDateFormat: date => formatClubDate(date, locale, {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    }),
    agendaHeaderFormat: ({
      start,
      end
    }) => `${formatClubDate(start, locale)} - ${formatClubDate(end, locale)}`,
    agendaTimeFormat: date => formatClubDate(date, locale, {
      hour: '2-digit',
      minute: '2-digit'
    }),
    agendaTimeRangeFormat: ({
      start,
      end
    }) => `${formatClubDate(start, locale, {
      hour: '2-digit',
      minute: '2-digit'
    })} - ${formatClubDate(end, locale, {
      hour: '2-digit',
      minute: '2-digit'
    })}`,
    dayFormat: date => formatClubDate(date, locale, {
      day: '2-digit',
      weekday: 'short'
    }),
    dayHeaderFormat: date => formatClubDate(date, locale, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }),
    dayRangeHeaderFormat: ({
      start,
      end
    }) => `${formatClubDate(start, locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })} - ${formatClubDate(end, locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })}`,
    monthHeaderFormat: date => formatClubDate(date, locale, {
      month: 'long',
      year: 'numeric'
    }),
    weekdayFormat: date => formatClubDate(date, locale, {
      weekday: 'short'
    })
  };
}

// ---------- helpers ----------
function toDateSafe(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') return new Date(v);
  return null;
}

// booking.date represents a London calendar day, so derive the UK date first.
function buildLocalDateFromBookingDate(dateLike, hhmm /* 'HH:MM' or null */) {
  const dateIso = formatUKDateISO(dateLike);
  if (!dateIso) return null;
  return ukDateISOToLocalDate(dateIso, hhmm || '00:00');
}
function colorForIndex(i) {
  const hue = i * 57 % 360;
  return `hsl(${hue} 70% 45%)`;
}
function pickMemberLabel(memberData = {}) {
  const first = typeof memberData.firstName === 'string' ? memberData.firstName.trim() : '';
  const last = typeof memberData.lastName === 'string' ? memberData.lastName.trim() : '';
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const displayName = (typeof memberData.displayName === 'string' && memberData.displayName.trim()) || (typeof memberData.username === 'string' && memberData.username.trim()) || '';
  if (displayName) return displayName;
  if (typeof memberData.email === 'string' && memberData.email.trim()) return memberData.email.trim();
  return '';
}
function summarizeCrewAssignments(assignments = [], memberNamesById = {}) {
  const rows = Array.isArray(assignments) ? assignments : [];
  const summary = rows
    .map((assignment) => {
      const memberId = typeof assignment?.memberId === 'string' ? assignment.memberId.trim() : '';
      const displayName = typeof assignment?.displayName === 'string' ? assignment.displayName.trim() : '';
      const seatLabel = typeof assignment?.seatLabel === 'string' ? assignment.seatLabel.trim() : '';
      const label = displayName || memberNamesById[memberId] || 'Crew member';
      return seatLabel ? `${label} (${seatLabel})` : label;
    })
    .filter(Boolean);
  return summary.length ? summary.join(', ') : '';
}
function normalizeTagList(input) {
  if (!Array.isArray(input)) return [];
  return Array.from(new Set(input.filter(tag => typeof tag === 'string').map(tag => tag.trim()).filter(Boolean)));
}
function memberTagsToSet(memberTags) {
  if (Array.isArray(memberTags)) {
    return new Set(memberTags.filter(tag => typeof tag === 'string').map(tag => tag.trim()).filter(Boolean));
  }
  if (memberTags && typeof memberTags === 'object') {
    return new Set(Object.entries(memberTags).filter(([name, enabled]) => Boolean(enabled) && typeof name === 'string').map(([name]) => name.trim()).filter(Boolean));
  }
  return new Set();
}
function shortTimeLabel(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addLocalDays(date, count) {
  const d = new Date(date);
  d.setDate(d.getDate() + count);
  return d;
}
function maxConcurrentEvents(events) {
  const points = [];
  events.forEach(event => {
    if (!(event?.start instanceof Date) || !(event?.end instanceof Date)) return;
    points.push({
      t: event.start.getTime(),
      delta: 1
    });
    points.push({
      t: event.end.getTime(),
      delta: -1
    });
  });
  points.sort((a, b) => {
    if (a.t === b.t) return a.delta - b.delta; // end before start at same instant
    return a.t - b.t;
  });
  let current = 0;
  let peak = 0;
  points.forEach(point => {
    current += point.delta;
    if (current > peak) peak = current;
  });
  return peak;
}
export default function BoatCalendarModal({
  isOpen,
  onClose,
  teamId,
  boats = [],
  selectedBoatId,
  fleet = 'rowing',
}) {
  const [loading, setLoading] = useState(false);
  const [allBookings, setAllBookings] = useState([]); // raw bookings across boats
  const [memberNamesById, setMemberNamesById] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  const [showFleetFilters, setShowFleetFilters] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState('');
  const [canCancelOthers, setCanCancelOthers] = useState(false);
  const [canManagePayments, setCanManagePayments] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [boatCharges, setBoatCharges] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => selectedBoatId ? new Set([selectedBoatId]) : new Set());
  const [viewDate, setViewDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [clubLocale, setClubLocale] = useState(DEFAULT_CALENDAR_LOCALE);
  const boatCalendarFormats = useMemo(() => buildBoatCalendarFormats(clubLocale), [clubLocale]);

  // Assign stable colours per boat
  const boatIndex = useMemo(() => {
    const map = {};
    boats.forEach((b, i) => {
      map[b.id] = {
        ...b,
        color: colorForIndex(i)
      };
    });
    return map;
  }, [boats]);
  useEffect(() => {
    if (!isOpen || !teamId) return;
    setLoading(true);
    setSelectedEvent(null);
    setDetailsCollapsed(false);
    setCancelling(false);
    setCancelError('');
    setCancelSuccess('');
    setCanCancelOthers(false);
    setSelectedCharge(null);
    setPaymentBusy(false);
    setBoatCharges([]);
    (async () => {
      try {
        const boatCollection = fleet === 'sailing' ? 'sailingBoats' : 'boats';
        const aggregated = [];
        const bookingSnaps = await Promise.all(boats.map(b => getDocsFromServer(collection(db, 'teams', teamId, boatCollection, b.id, 'bookings'))));
        bookingSnaps.forEach((snap, index) => {
          const boat = boats[index];
          snap.forEach(docSnap => {
            aggregated.push({
              id: docSnap.id,
              boatId: boat?.id || null,
              ...docSnap.data()
            });
          });
        });
        const bookedMemberIds = Array.from(new Set(aggregated.map(r => typeof r?.memberId === 'string' ? r.memberId : '').filter(Boolean)));
        const membersMap = {};
        try {
          const membersSnap = await getDocs(collection(db, 'teams', teamId, 'members'));
          membersSnap.forEach(docSnap => {
            const label = pickMemberLabel(docSnap.data() || {});
            if (label) membersMap[docSnap.id] = label;
          });
        } catch {
          // Non-admins cannot list all members; fallback labels are used.
        }
        const unresolvedIds = bookedMemberIds.filter(memberId => !membersMap[memberId]);
        if (unresolvedIds.length > 0) {
          const userSnaps = await Promise.all(unresolvedIds.map(uid => getDoc(doc(db, 'users', uid))));
          userSnaps.forEach(userSnap => {
            if (!userSnap.exists()) return;
            const label = pickMemberLabel(userSnap.data() || {});
            if (label) membersMap[userSnap.id] = label;
          });
        }
        setMemberNamesById(membersMap);
        setAllBookings(aggregated);
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, teamId, boats, fleet]);
  useEffect(() => {
    if (!isOpen || !teamId) {
      setClubLocale(DEFAULT_CALENDAR_LOCALE);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        if (cancelled) return;
        setClubLocale(localeForCountry(teamSnap.data()?.address?.country));
      } catch {
        if (!cancelled) setClubLocale(DEFAULT_CALENDAR_LOCALE);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);
  useEffect(() => {
    if (!isOpen || !teamId) return;
    const uid = getAuth().currentUser?.uid || null;
    if (!uid) {
      setCanCancelOthers(false);
      return;
    }
    (async () => {
      try {
        const [teamSnap, memberSnap, assignmentSnap] = await Promise.all([
          getDoc(doc(db, 'teams', teamId)),
          getDoc(doc(db, 'teams', teamId, 'members', uid)),
          getDoc(doc(db, 'teams', teamId, 'adminAssignments', uid)).catch(() => null),
        ]);
        const rules = fleet === 'sailing'
          ? (teamSnap.data()?.sailing?.boats?.rules || teamSnap.data()?.sailing?.rules || {})
          : (teamSnap.data()?.rowing?.rules || {});
        const overrideTags = normalizeTagList(rules.cancelAnyTagOverrides);
        const memberTags = memberTagsToSet(memberSnap.data()?.tags);
        setCanCancelOthers(overrideTags.some(tagName => memberTags.has(tagName)));
        const fullAdmin = Array.isArray(teamSnap.data()?.admins) && teamSnap.data().admins.includes(uid);
        const keys = Array.isArray(assignmentSnap?.data?.()?.permissionKeys) ? assignmentSnap.data().permissionKeys : [];
        setCanManagePayments(fullAdmin || keys.includes('sailingBoatBooking.manage') || keys.includes('finance.manage'));
      } catch {
        setCanCancelOthers(false);
        setCanManagePayments(false);
      }
    })();
  }, [isOpen, teamId, fleet]);
  useEffect(() => {
    if (!isOpen || fleet !== 'sailing' || !canManagePayments || !teamId) return;
    getDocs(collection(db, 'teams', teamId, 'sailingBoatCharges'))
      .then((snap) => setBoatCharges(snap.docs.map((row) => ({ id: row.id, ...row.data() }))
        .sort((left, right) => Number(right.createdAt?.seconds || 0) - Number(left.createdAt?.seconds || 0))
        .slice(0, 50)))
      .catch(() => setBoatCharges([]));
  }, [canManagePayments, fleet, isOpen, teamId]);
  const events = useMemo(() => {
    if (!allBookings.length) return [];
    const show = selectedIds.size ? selectedIds : new Set(boats.map(b => b.id));
    return allBookings.filter(r => r.boatId && show.has(r.boatId)).map(r => {
      const date = toDateSafe(r.date); // UTC midnight of the UK day
      const start = buildLocalDateFromBookingDate(date, r.fromTime || '00:00');
      const end = buildLocalDateFromBookingDate(date, r.toTime || '23:59' // treat null as full-day
      );
      const boat = boatIndex[r.boatId];
      const boatName = boat?.name || 'Boat';
      const title = r.title || boatName;
      return {
        id: r.id,
        title,
        start,
        end,
        allDay: !r.fromTime && !r.toTime,
        boatId: r.boatId,
        memberId: r.memberId || null,
        requestId: r.requestId || null,
        chargeId: r.chargeId || null,
        paymentStatus: r.paymentStatus || '',
        source: r.source || 'allocation',
        courseType: r.courseType || '',
        courseId: r.courseId || '',
        sessionId: r.sessionId || '',
        courseName: r.courseName || '',
        sessionName: r.sessionName || '',
        courseState: r.courseState || 'live',
        status: r.status || 'reserved',
        _boatName: boat?.name || r.boatId,
        _color: boat?.color,
        _memberName: r.responsibleMemberName || summarizeCrewAssignments(r.crewAssignments, memberNamesById) || memberNamesById[r.memberId] || (r.source === 'outing' ? 'Outing crew' : 'Unknown member')
      };
    }).filter(e => e.start && e.end);
  }, [allBookings, selectedIds, boatIndex, boats, memberNamesById]);
  useEffect(() => {
    if (!selectedEvent) return;
    const stillVisible = events.some(event => event.id === selectedEvent.id && event.boatId === selectedEvent.boatId);
    if (!stillVisible) setSelectedEvent(null);
  }, [events, selectedEvent]);
  const authUid = getAuth().currentUser?.uid || null;
  const isOwnSelection = Boolean(authUid && selectedEvent?.memberId === authUid);
  const canCancelSelection = Boolean(!['outing', 'course'].includes(selectedEvent?.source) && selectedEvent?.id && selectedEvent?.boatId && (isOwnSelection || canCancelOthers));
  useEffect(() => {
    if (fleet !== 'sailing' || !selectedEvent?.chargeId || !teamId) {
      setSelectedCharge(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'teams', teamId, 'sailingBoatCharges', selectedEvent.chargeId))
      .then((snap) => { if (!cancelled) setSelectedCharge(snap.exists() ? { id: snap.id, ...snap.data() } : null); })
      .catch(() => { if (!cancelled) setSelectedCharge(null); });
    return () => { cancelled = true; };
  }, [fleet, selectedEvent?.chargeId, teamId]);

  const waiveSelectedCharge = async (charge = selectedCharge) => {
    if (!charge?.id || paymentBusy || !window.confirm('Waive this outstanding boat charge?')) return;
    setPaymentBusy(true);
    setCancelError('');
    try {
      await httpsCallable(functions, 'waiveSailingBoatCharge')({ teamId, chargeId: charge.id });
      setSelectedCharge((current) => current?.id === charge.id ? ({ ...current, status: 'waived', blocksFutureBookings: false }) : current);
      setBoatCharges((current) => current.map((row) => row.id === charge.id ? { ...row, status: 'waived', blocksFutureBookings: false } : row));
      setCancelSuccess('Boat charge waived.');
    } catch (err) {
      setCancelError(err?.message || 'Could not waive this charge.');
    } finally {
      setPaymentBusy(false);
    }
  };

  const refundSelectedCharge = async (charge = selectedCharge) => {
    if (!charge?.id || paymentBusy) return;
    const remainingMinor = Math.max(0, Number(charge.paidAmountMinor || charge.checkoutAmountMinor || 0) - Number(charge.refundedAmountMinor || 0));
    const factor = ['JPY', 'KRW', 'VND'].includes(String(charge.currency || '').toUpperCase()) ? 1 : 100;
    const entered = window.prompt(`Refund amount in ${charge.currency || 'GBP'}`, String(remainingMinor / factor));
    if (entered == null) return;
    const amountMinor = Math.round(Number(entered) * factor);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0 || amountMinor > remainingMinor) {
      setCancelError('Enter a valid amount no greater than the refundable balance.');
      return;
    }
    setPaymentBusy(true);
    setCancelError('');
    try {
      const result = await httpsCallable(functions, 'refundSailingBoatCharge')({ teamId, chargeId: charge.id, amountMinor });
      const refunded = Number(charge.refundedAmountMinor || 0) + Number(result.data?.amountMinor || amountMinor);
      const status = result.data?.remainingRefundableMinor > 0 ? 'partially_refunded' : 'refunded';
      setSelectedCharge((current) => current?.id === charge.id ? ({ ...current, refundedAmountMinor: refunded, status }) : current);
      setBoatCharges((current) => current.map((row) => row.id === charge.id ? { ...row, refundedAmountMinor: refunded, status } : row));
      setCancelSuccess('Boat payment refund submitted.');
    } catch (err) {
      setCancelError(err?.message || 'Could not refund this charge.');
    } finally {
      setPaymentBusy(false);
    }
  };
  const handleCancelSelected = async () => {
    if (!teamId || !selectedEvent?.id || !selectedEvent?.boatId || cancelling) return;
    setCancelError('');
    setCancelSuccess('');
    if (!isOwnSelection && !canCancelOthers) {
      setCancelError('You need a cancel override tag to cancel another member\'s booking.');
      return;
    }
    const ok = window.confirm(`Cancel this booking for ${selectedEvent._memberName}?`);
    if (!ok) return;
    try {
      setCancelling(true);
      const cancelFn = httpsCallable(functions, fleet === 'sailing' ? 'cancelSailingBoatRequest' : 'cancelBoatRequest');
      const payload = {
        teamId,
        bookingId: selectedEvent.id,
        boatId: selectedEvent.boatId,
        soft: true
      };
      if (selectedEvent.requestId) payload.requestId = selectedEvent.requestId;
      await cancelFn(payload);
      setAllBookings(prev => prev.filter(booking => !(booking.id === selectedEvent.id && booking.boatId === selectedEvent.boatId)));
      setCancelSuccess('Booking cancelled.');
      setSelectedEvent(null);
    } catch (err) {
      setCancelError(err?.message || 'Could not cancel booking.');
    } finally {
      setCancelling(false);
    }
  };

  // Legend + checkboxes, focus boat first
  const boatOptions = useMemo(() => {
    const list = [...boats];
    list.sort((a, b) => {
      if (a.id === selectedBoatId) return -1;
      if (b.id === selectedBoatId) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return list.map((b, i) => ({
      id: b.id,
      name: b.name || 'Unnamed',
      color: boatIndex[b.id]?.color || colorForIndex(i)
    }));
  }, [boats, selectedBoatId, boatIndex]);
  const eventPropGetter = event => {
    const bg = event._color || '#2563eb';
    return {
      style: {
        backgroundColor: bg,
        borderColor: bg,
        color: '#fff'
      }
    };
  };
  const shownBoatCount = selectedIds.size === 0 ? boatOptions.length : selectedIds.size;
  const isBoatShown = boatId => selectedIds.size === 0 ? true : selectedIds.has(boatId);
  const toggleBoatShown = (boatId, checked) => {
    setSelectedIds(prev => {
      // Empty set is treated as "all boats shown"
      if (prev.size === 0) {
        if (checked) return prev;
        const next = new Set(boats.map(b => b.id));
        next.delete(boatId);
        return next;
      }
      const next = new Set(prev);
      if (checked) next.add(boatId);else next.delete(boatId);

      // Canonicalize back to empty set when all are selected.
      if (next.size >= boats.length) return new Set();
      return next;
    });
  };
  const densityHint = useMemo(() => {
    if (!events.length || view === 'agenda') return null;
    if (view === 'month') {
      const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
      const monthEnd = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
      const countsByDay = new Map();
      events.forEach(event => {
        if (!(event.start instanceof Date) || !(event.end instanceof Date)) return;
        if (event.end <= monthStart || event.start >= monthEnd) return;
        const dayStart = startOfLocalDay(event.start);
        const key = formatLocalDateISO(dayStart);
        countsByDay.set(key, (countsByDay.get(key) || 0) + 1);
      });
      const entries = Array.from(countsByDay.entries());
      if (!entries.length) return null;
      const crowdedDays = entries.filter(([, count]) => count >= 6).length;
      const [worstDayKey, worstCount] = entries.reduce((best, current) => current[1] > best[1] ? current : best, entries[0]);
      if (crowdedDays === 0 && worstCount < 8) return null;
      return {
        message: `${crowdedDays || 1} day${(crowdedDays || 1) === 1 ? '' : 's'} this month ${crowdedDays ? 'have' : 'has'} heavy booking density. Agenda list is easier to scan.`,
        suggestDate: new Date(`${worstDayKey}T12:00:00`)
      };
    }
    if (view === 'day') {
      const dayStart = startOfLocalDay(viewDate);
      const dayEnd = addLocalDays(dayStart, 1);
      const dayEvents = events.filter(event => event.start instanceof Date && event.end instanceof Date && event.end > dayStart && event.start < dayEnd);
      const timedEvents = dayEvents.filter(event => !event.allDay);
      const peak = maxConcurrentEvents(timedEvents);
      if (dayEvents.length < 12 && peak < 7) return null;
      return {
        message: `${dayEvents.length} bookings on this day (up to ${peak} at once). Agenda list will be easier to read.`,
        suggestDate: dayStart
      };
    }
    if (view === 'week') {
      const weekStart = startOfWeek(viewDate, {
        weekStartsOn: 1
      });
      const stats = Array.from({
        length: 7
      }).map((_, index) => {
        const day = addLocalDays(startOfLocalDay(weekStart), index);
        const dayEnd = addLocalDays(day, 1);
        const dayEvents = events.filter(event => event.start instanceof Date && event.end instanceof Date && event.end > day && event.start < dayEnd);
        const timedEvents = dayEvents.filter(event => !event.allDay);
        return {
          day,
          count: dayEvents.length,
          peak: maxConcurrentEvents(timedEvents)
        };
      });
      const worst = stats.reduce((best, current) => {
        if (!best) return current;
        if (current.peak !== best.peak) return current.peak > best.peak ? current : best;
        return current.count > best.count ? current : best;
      }, null);
      const totalWeekEvents = stats.reduce((sum, day) => sum + day.count, 0);
      if (!worst) return null;
      if (worst.count < 10 && worst.peak < 6 && totalWeekEvents < 35) return null;
      return {
        message: `${format(worst.day, 'EEE d MMM')} is crowded (${worst.count} bookings, peak ${worst.peak} concurrent). Agenda list is recommended.`,
        suggestDate: worst.day
      };
    }
    return null;
  }, [events, view, viewDate]);
  if (!isOpen) return null;
  return <div className="modal-backdrop calendar-modal" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">{fleet === 'sailing' ? 'Sailing Boat Calendar' : 'Boat Calendar'}</h2>
          <button onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Controls */}
        <section className="controls-row" style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
          <strong>Show boats:</strong>
          <button type="button" className="btn btn--secondary btn--xs" onClick={() => setSelectedIds(new Set())}>
            All
          </button>
          <button type="button" className="btn btn--secondary btn--xs" onClick={() => setSelectedIds(selectedBoatId ? new Set([selectedBoatId]) : new Set())} title="Show only the clicked boat">
            Focus
          </button>
          <button type="button" className="btn btn--secondary btn--xs" onClick={() => setSelectedIds(new Set())} title="Clear selection (shows all by default)">
            Clear
          </button>

          <div className="calendar-controls-right">
            <div className="calendar-view-toggle-group" style={{
            display: 'inline-flex',
            gap: 6
          }}>
              {['month', 'week', 'day', 'agenda'].map(viewKey => <button key={viewKey} type="button" className={view === viewKey ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'} onClick={() => setView(viewKey)}>
                  {viewKey.charAt(0).toUpperCase() + viewKey.slice(1)}
                </button>)}
            </div>
            <span className="text-muted" style={{
            fontSize: '0.9rem'
          }}>
              Showing {shownBoatCount}/{boatOptions.length}
            </span>
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => setShowFleetFilters(prev => !prev)} title="Toggle fleet filter list">
              {showFleetFilters ? 'Hide fleet filter' : 'Show fleet filter'}
            </button>
          </div>
        </section>

        {densityHint && <section className="calendar-density-hint">
            <span>{densityHint.message}</span>
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => {
          if (densityHint.suggestDate) setViewDate(densityHint.suggestDate);
          setView('agenda');
          setSelectedEvent(null);
          setDetailsCollapsed(false);
        }}>
              Switch to agenda list
            </button>
          </section>}

        {showFleetFilters && <section className="calendar-fleet-panel">
            {boatOptions.map(opt => <label key={opt.id} className="calendar-fleet-option">
                <input type="checkbox" checked={isBoatShown(opt.id)} onChange={e => toggleBoatShown(opt.id, e.target.checked)} />
                <span aria-hidden style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: opt.color,
            display: 'inline-block'
          }} />
                <span>{opt.name}</span>
              </label>)}
          </section>}

        {cancelError && <div className="modal-error">{cancelError}</div>}
        {cancelSuccess && <p className="muted">{cancelSuccess}</p>}

        {fleet === 'sailing' && canManagePayments && boatCharges.length > 0 && <details className="calendar-payment-ledger">
          <summary>Boat payments ({boatCharges.length})</summary>
          <div className="calendar-payment-ledger__rows">
            {boatCharges.map((charge) => <div key={charge.id} className="list-row calendar-payment-ledger__row">
              <span>
                <strong>{charge.boatName || 'Sailing boat booking'}</strong>
                <small className="text-muted">{memberNamesById[charge.memberId] || 'Club member'} · {String(charge.status || 'scheduled').replaceAll('_', ' ')} · {formatSailingBoatMoney(charge.checkoutAmountMinor || charge.baseAmountMinor, charge.currency)}</small>
              </span>
              <span className="form-actions">
                {['scheduled', 'unpaid', 'failed', 'checkout_pending'].includes(charge.status) && <button type="button" className="btn btn--secondary btn--xs" onClick={() => waiveSelectedCharge(charge)} disabled={paymentBusy}>Waive</button>}
                {['paid', 'partially_refunded'].includes(charge.status) && <button type="button" className="btn btn--secondary btn--xs" onClick={() => refundSelectedCharge(charge)} disabled={paymentBusy}>Refund</button>}
              </span>
            </div>)}
          </div>
        </details>}

        {/* Calendar */}
        {loading ? <p className="muted">Loading bookings…</p> : <div className="calendar-shell">
            <div className={`calendar-container calendar-container--${view}`} style={{
          flex: 1,
          minHeight: 0
        }}>
              <Calendar localizer={localizer} events={events} startAccessor="start" endAccessor="end" style={{
            height: '100%'
          }} culture={clubLocale} formats={boatCalendarFormats} view={view} date={viewDate} onView={setView} onNavigate={setViewDate} onDrillDown={(nextDate, nextView) => {
            setViewDate(nextDate);
            setView(nextView || 'day');
            setSelectedEvent(null);
            setDetailsCollapsed(false);
          }} defaultView="month" views={['month', 'week', 'day', 'agenda']} length={14} popup={false} drilldownView="day" doShowMoreDrillDown dayLayoutAlgorithm="no-overlap" onShowMore={(_, nextDate) => {
            setViewDate(nextDate);
            setView('day');
            setSelectedEvent(null);
            setDetailsCollapsed(false);
          }} onSelectEvent={event => {
            setSelectedEvent(event);
            setDetailsCollapsed(false);
          }} eventPropGetter={eventPropGetter} tooltipAccessor={e => {
            const time = e.allDay ? '(all day)' : `${String(e.start.getHours()).padStart(2, '0')}:${String(e.start.getMinutes()).padStart(2, '0')}–${String(e.end.getHours()).padStart(2, '0')}:${String(e.end.getMinutes()).padStart(2, '0')}`;
            return `${e._boatName} — ${time} — ${e._memberName} — ${e.status}`;
          }} components={{
            event: ({
              event
            }) => <span title={`${event._boatName} — ${event._memberName} — ${event.status}`}>
                      {view === 'month' ? `${event._boatName}${event.allDay ? '' : ` ${shortTimeLabel(event.start)}`}` : `${event._boatName} — ${event._memberName}`}
                    </span>,
            agenda: {
              event: ({
                event
              }) => <span title={`${event._boatName} — ${event._memberName} — ${event.status}`}>
                        {`${event._boatName} — ${event._memberName} (${event.status})`}
                      </span>
            }
          }} />
            </div>

            {selectedEvent && <aside className={`calendar-details-overlay ${detailsCollapsed ? 'collapsed' : ''}`}>
                <div className="calendar-details-head">
                  <h3>{detailsCollapsed ? 'Reservation' : 'Reservation details'}</h3>
                  <div className="calendar-details-actions">
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => setDetailsCollapsed(prev => !prev)}>
                      {detailsCollapsed ? 'Expand' : 'Collapse'}
                    </button>
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => setSelectedEvent(null)}>
                      Hide
                    </button>
                  </div>
                </div>

                {!detailsCollapsed && <div className="calendar-details-body">
                    <div><strong>{selectedEvent.source === 'outing' ? 'Crew:' : selectedEvent.source === 'course' ? 'Responsible member:' : 'Booked by:'}</strong> {selectedEvent._memberName}</div>
                    <div><strong>Boat:</strong> {selectedEvent._boatName}</div>
                    <div><strong>Date:</strong> {format(selectedEvent.start, 'EEE d MMM yyyy')}</div>
                    <div>
                      <strong>Time:</strong> {selectedEvent.allDay ? 'All day' : `${format(selectedEvent.start, 'HH:mm')} - ${format(selectedEvent.end, 'HH:mm')}`}
                    </div>
                    <div><strong>Status:</strong> {selectedEvent.status}</div>
                    {selectedCharge && <div className="calendar-payment-details">
                      <div><strong>Payment:</strong> {String(selectedCharge.status || 'scheduled').replaceAll('_', ' ')}</div>
                      <div><strong>Amount:</strong> {formatSailingBoatMoney(selectedCharge.checkoutAmountMinor || selectedCharge.baseAmountMinor, selectedCharge.currency)}</div>
                      {Number(selectedCharge.refundedAmountMinor || 0) > 0 && <div><strong>Refunded:</strong> {formatSailingBoatMoney(selectedCharge.refundedAmountMinor, selectedCharge.currency)}</div>}
                      {canManagePayments && <div className="form-actions">
                        {['scheduled', 'unpaid', 'failed', 'checkout_pending'].includes(selectedCharge.status) && <button type="button" className="btn btn--secondary btn--xs" onClick={() => waiveSelectedCharge()} disabled={paymentBusy}>Waive charge</button>}
                        {['paid', 'partially_refunded'].includes(selectedCharge.status) && <button type="button" className="btn btn--secondary btn--xs" onClick={() => refundSelectedCharge()} disabled={paymentBusy}>Refund</button>}
                      </div>}
                    </div>}
                    {selectedEvent.source === 'course' && <>
                      <div><strong>Course:</strong> {selectedEvent.courseName || 'Course'}</div>
                      <div><strong>Session:</strong> {selectedEvent.sessionName || 'Course session'}</div>
                      <div><strong>Reservation:</strong> {selectedEvent.courseState === 'draft' ? 'Provisional draft' : 'Live'}</div>
                      <div className="text-muted" style={{ marginTop: 6 }}>
                        Managed through the linked course resource plan.
                      </div>
                      <a className="btn btn--secondary btn--xs" href={`/teams/${encodeURIComponent(teamId)}?courseType=${encodeURIComponent(selectedEvent.courseType)}&course=${encodeURIComponent(selectedEvent.courseId)}&resources=1`} style={{ marginTop: 10 }}>
                        Edit course reservation
                      </a>
                    </>}
                    {selectedEvent.source === 'outing' && <div className="text-muted" style={{
              marginTop: 6
            }}>
                        Managed through the linked outing workflow.
                      </div>}
                    {canCancelSelection && <div style={{
              marginTop: 12
            }}>
                        <button type="button" className="btn btn--secondary btn--xs" onClick={handleCancelSelected} disabled={cancelling} title={isOwnSelection ? 'Cancel your booking' : 'Cancel this booking via override permission'}>
                          {cancelling ? 'Cancelling…' : isOwnSelection ? 'Cancel booking' : 'Override & cancel booking'}
                        </button>
                      </div>}
                  </div>}
              </aside>}
          </div>}
      </div>
    </div>;
}
